import type React from "react"
import type { FC } from "react"
import { Trans, useTranslation } from "react-i18next"
import type { Offer } from "../../offersList/offersListTypes"

interface NewsletterSelectedOffersProps {
  selected: Offer[]
  draggingIndex: number | null
  onDragStart: (index: number) => (e: React.DragEvent<HTMLLIElement>) => void
  onDragOver: (e: React.DragEvent<HTMLLIElement>) => void
  onDrop: (toIndex: number) => (e: React.DragEvent<HTMLLIElement>) => void
  onDragEnd: () => void
  onEdit: (offerId: string) => void
  onRemove: (offer: Offer) => void
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  onClear: () => void
}

const NewsletterSelectedOffers: FC<NewsletterSelectedOffersProps> = ({
  selected,
  draggingIndex,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onEdit,
  onRemove,
  onMoveUp,
  onMoveDown,
  onClear,
}) => {
  const { t } = useTranslation()

  return (
    <div className="mb-3 rounded-md border border-gray-200 bg-white">
      <div className="px-3 py-2 border-b border-gray-200 text-sm text-gray-700 flex items-center justify-between">
        <span>
          <Trans i18nKey="newsletter.selected.title" />
        </span>
        <button
          className="text-xs text-red-600 hover:text-red-800"
          onClick={onClear}
        >
          <Trans i18nKey="newsletter.selected.clear" />
        </button>
      </div>
      <ul className="divide-y divide-gray-200">
        {selected.map((offer, idx) => (
          <li
            key={`${offer.Type}-${offer.CustomOfferID}`}
            className={`flex items-start justify-between px-3 py-2 ${draggingIndex === idx ? "bg-gray-50" : ""}`}
            draggable
            onDragStart={onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={onDrop(idx)}
            onDragEnd={onDragEnd}
          >
            <div className="flex items-center min-w-0 mr-3 gap-3">
              <img
                src={offer.ImageURL}
                alt={offer.OfferTitle}
                className="w-12 h-12 rounded border object-cover flex-shrink-0"
              />
              <div className="text-sm font-medium text-gray-900 truncate">
                {offer.OfferTitle}
              </div>
              <div className="text-xs text-gray-500 overflow-hidden whitespace-pre-line text-ellipsis">
                {offer.AccommodationName}
              </div>
            </div>
            <div className="flex items-center gap-1 self-center">
              <button
                aria-label={t("newsletter.actions.editAria")}
                className="inline-flex items-center justify-center rounded border px-2 py-1 text-xs hover:bg-gray-50"
                onClick={() => onEdit(offer.CustomOfferID)}
              >
                <Trans i18nKey="newsletter.actions.edit" />
              </button>
              <button
                aria-label={t("newsletter.actions.removeAria")}
                className="inline-flex items-center justify-center rounded border px-2 py-1 text-xs hover:bg-gray-50 text-red-700"
                onClick={() => onRemove(offer)}
              >
                <Trans i18nKey="newsletter.actions.remove" />
              </button>
              <button
                aria-label={t("newsletter.actions.moveUpAria")}
                className={`inline-flex items-center justify-center rounded border px-2 py-1 text-xs ${idx === 0 ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50"}`}
                onClick={() => onMoveUp(idx)}
                disabled={idx === 0}
              >
                ↑
              </button>
              <button
                aria-label={t("newsletter.actions.moveDownAria")}
                className={`inline-flex items-center justify-center rounded border px-2 py-1 text-xs ${idx === selected.length - 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50"}`}
                onClick={() => onMoveDown(idx)}
                disabled={idx === selected.length - 1}
              >
                ↓
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default NewsletterSelectedOffers
